import {
  Box,
  Button,
  Container,
  Heading,
  Text,
  useColorModeValue,
  VStack,
  HStack,
  Flex,
  Badge,
  Icon,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Grid,
  GridItem,
  List,
  ListItem,
  Avatar,
  // Tooltip,
  // useToast,
  ScaleFade,
} from "@chakra-ui/react";
import { IoMdArrowForward, IoIosBody, IoMdDocument } from "react-icons/io";
import {
  MdOutlineAccessTimeFilled,
  MdOutlineMedicalServices,
} from "react-icons/md";
import {
  FaHeartbeat,
  FaFileMedicalAlt,
  FaStethoscope,
  FaSyringe,
  FaHistory,
  FaClipboardList,
  FaProcedures,
  FaHospitalAlt,
  FaUser,
  FaBriefcaseMedical,
  // FaDownload,
  // FaShare,
  // FaPrint,
} from "react-icons/fa";

import Footer from "../Common/Footer";
import Header from "../Common/Header";
import { handleCTA } from "../../utils";

// Case Data
const CASE_STUDIES = [
  {
    id: "case-1",
    age: 59,
    gender: "Male",
    title: "Prostate Cancer Evaluation",
    tag: "Urology",
    chiefComplaint: "Difficulty in urination for 2-3 years",
    diagnosis: "Prostate cancer",
    symptoms: ["Difficulty in urination", "Weight loss", "Loss of appetite"],
    vitalSigns: {
      pulseRate: "80 beats per minute",
      bp: "140/90 mmHg",
      saturation: "98%",
    },
    historyOfIllness: [
      "Hypertension for 3 years (controlled with medication)",
      "Previous cataract surgery under local anesthesia",
      "Multiple medication allergies",
    ],
    medications: ["Telmisartan 40mg", "Hydrochlorothiazide 12.5mg"],
    allergies: [
      "Cephalosporin",
      "Diclofenac",
      "Oxacillin",
      "Ampicillin",
      "Ofloxacin",
      "Sulfa",
      "Amoxicillin",
      "Ciprofloxacin",
    ],
    treatmentPlan: "Prostate biopsy under total intravenous anesthesia",
    labResults: {
      hb: "11.6",
      pc: "37.9",
      platelet: "310,000",
      sodium: "Normal",
      potassium: "Normal",
      bun: "Normal",
      creatinine: "Normal",
    },
    additionalNotes: "Mallampati grade 4 - difficult intubation anticipated",
  },
  {
    id: "case-2",
    age: 70,
    gender: "Male",
    title: "Umbilical Hernia Repair",
    tag: "General Surgery",
    chiefComplaint: "Umbilical hernia",
    diagnosis: "Umbilical hernia requiring surgical repair",
    symptoms: ["Abdominal discomfort (inferred)"],
    vitalSigns: {
      pulseRate: "Not specified",
      bp: "Not specified",
      saturation: "Not specified",
    },
    historyOfIllness: ["History of smoking", "No other comorbidities"],
    medications: [],
    allergies: [],
    treatmentPlan:
      "Surgical repair of umbilical hernia under spinal anesthesia",
    anesthesiaDetails: {
      primary:
        "Injection bupivacaine 3.5cc (0.5% heavy) with 25 micrograms fentanyl",
      rescue:
        "Paracetamol 1g + Tramadol 100mg, followed by Fentanyl 50mcg + 50mg, and Ketamine 20mg",
    },
    procedureOutcome:
      "Procedure completed in one hour, uneventful, patient shifted to post-op",
  },
  {
    id: "case-3",
    age: 45,
    gender: "Male",
    title: "Polytrauma from Road Traffic Accident",
    tag: "Emergency",
    chiefComplaint: "Multiple injuries from road traffic accident",
    diagnosis: "Polytrauma with multiple fractures and blunt abdominal trauma",
    symptoms: ["Severe hypotension", "Altered consciousness (GCS 7/15)"],
    vitalSigns: {
      initialBP: "Severely hypotensive",
      intraopBP: "Dropped to 40/20 mmHg during procedure",
      finalBP: "130/65 mmHg (on vasopressors)",
    },
    historyOfIllness: [],
    injuries: [
      "Facial injuries",
      "Fractures of tibia and fibula (bilateral)",
      "Fracture of left radius and ulna",
      "Blunt abdominal trauma with internal bleeding",
    ],
    treatmentPlan: [
      "Emergency stabilization",
      "Intubation and mechanical ventilation",
      "Exploratory laparotomy",
      "Control of intra-abdominal bleeding",
      "Wound cleaning and suturing of facial injuries",
      "Temporary stabilization of fractures",
    ],
    interventions: [
      "Central venous line placement",
      "Arterial line insertion",
      "Blood transfusion (4 units)",
      "Fluid resuscitation (3 units of Ringer's lactate)",
      "Vasopressor support (noradrenaline)",
    ],
    disposition: "Transferred to ICU on mechanical ventilation",
  },
];

function ClinicalSummaries() {
  const bgGradient = "linear(to-r, teal.800, blue.800)";
  const cardBg = useColorModeValue("white", "gray.800");
  const sectionBg = useColorModeValue("gray.50", "gray.900");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const highlightColor = useColorModeValue("teal.700", "teal.200");
  // const toast = useToast();

  const tagBg = {
    Urology: "purple.100",
    "General Surgery": "blue.100",
    Emergency: "red.100",
  } as const;

  const tagColor = {
    Urology: "purple.700",
    "General Surgery": "blue.700",
    Emergency: "red.700",
  } as const;

  // const handleAction = (action: string) => {
  //   toast({
  //     title: `${action} initiated`,
  //     description: "This feature will be available soon",
  //     status: "info",
  //     duration: 3000,
  //     isClosable: true,
  //   });
  // };

  return (
    <Box>
      <Header minimal={false} />

      {/* Enhanced Hero Section */}
      <Box
        as="section"
        bgGradient={bgGradient}
        py={{ base: 16, md: 20 }}
        color="white"
        position="relative"
        overflow="hidden"
      >
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          opacity={0.1}
          backgroundImage="url('/assets/patterns/medical-pattern.svg')"
          backgroundSize="cover"
        />
        <Container maxW="container.lg" position="relative">
          <VStack spacing={6} align={{ base: "center", md: "flex-start" }}>
            <Badge
              px={3}
              py={1}
              bg="whiteAlpha.200"
              color="white"
              fontWeight="medium"
              borderRadius="full"
              backdropFilter="blur(10px)"
            >
              AI-Powered Clinical Summarization
            </Badge>
            <Heading
              size="2xl"
              lineHeight="1.2"
              fontWeight="bold"
              maxW="container.md"
              textShadow="0 2px 4px rgba(0,0,0,0.1)"
            >
              Transform Medical Documentation with AI
            </Heading>
            <Text fontSize="lg" maxW="container.md" opacity={0.9}>
              Experience the future of clinical documentation with intelligent
              summarization that saves time and improves accuracy.
            </Text>
            <HStack spacing={4} pt={2}>
              <Button
                size="lg"
                bg="white"
                color="teal.700"
                _hover={{ transform: "translateY(-2px)", boxShadow: "lg" }}
                borderRadius="md"
                px={6}
                fontWeight="bold"
                onClick={() => handleCTA("signup")}
                rightIcon={<IoMdArrowForward />}
              >
                Start Free Trial
              </Button>
              <Button
                size="lg"
                variant="outline"
                borderColor="whiteAlpha.400"
                borderRadius="md"
                px={6}
                _hover={{ bg: "whiteAlpha.200" }}
                fontWeight="medium"
                onClick={() => {
                  const demoSection = document.getElementById("demo");
                  demoSection?.scrollIntoView({ behavior: "smooth" });
                }}
              >
                View Demo Cases
              </Button>
            </HStack>
          </VStack>
        </Container>
      </Box>

      {/* Enhanced Case Demonstration */}
      <Box as="section" py={16} id="demo">
        <Container maxW="container.xl">
          <VStack spacing={10}>
            <Box textAlign="center" maxW="container.md" mx="auto">
              <Badge
                colorScheme="teal"
                px={3}
                py={1}
                mb={4}
                borderRadius="full"
                fontSize="sm"
              >
                Interactive Case Viewer
              </Badge>
              <Heading
                as="h2"
                size="xl"
                color={highlightColor}
                fontWeight="bold"
                mb={4}
              >
                Real-World Clinical Cases
              </Heading>
              <Text fontSize="lg" color="gray.600">
                Explore how our AI transforms complex medical cases into
                structured, actionable summaries.
              </Text>
            </Box>

            <Box
              w="full"
              bg={sectionBg}
              borderRadius="2xl"
              p={6}
              boxShadow="xl"
              border="1px"
              borderColor={borderColor}
            >
              <Tabs variant="soft-rounded" colorScheme="teal" isFitted mb={6}>
                <TabList
                  mb="1em"
                  overflowX="auto"
                  css={{
                    scrollbarWidth: "none",
                    "&::-webkit-scrollbar": {
                      display: "none",
                    },
                  }}
                >
                  {CASE_STUDIES.map((caseStudy) => (
                    <Tab
                      key={caseStudy.id}
                      fontWeight="medium"
                      _selected={{ color: "white", bg: "teal.500" }}
                    >
                      <HStack>
                        <Icon as={FaFileMedicalAlt} />
                        <Text>{caseStudy.title}</Text>
                      </HStack>
                    </Tab>
                  ))}
                </TabList>

                <TabPanels>
                  {CASE_STUDIES.map((caseStudy) => (
                    <TabPanel key={caseStudy.id} p={0}>
                      <ScaleFade in={true} initialScale={0.9}>
                        <Box
                          bg={cardBg}
                          borderRadius="lg"
                          boxShadow="sm"
                          border="1px"
                          borderColor={borderColor}
                          overflow="hidden"
                        >
                          {/* Enhanced Case Header */}
                          <Flex
                            p={6}
                            borderBottom="1px"
                            borderColor={borderColor}
                            bg={sectionBg}
                            justify="space-between"
                            align="center"
                            wrap="wrap"
                            gap={4}
                          >
                            <VStack align="flex-start" spacing={1}>
                              <HStack>
                                <Avatar
                                  size="sm"
                                  bg={
                                    tagBg[caseStudy.tag as keyof typeof tagBg]
                                  }
                                  color={
                                    tagColor[
                                      caseStudy.tag as keyof typeof tagColor
                                    ]
                                  }
                                  icon={<FaUser />}
                                />
                                <Heading as="h3" size="md">
                                  {caseStudy.title}
                                </Heading>
                              </HStack>
                              <HStack>
                                <Badge colorScheme="gray" variant="subtle">
                                  {caseStudy.age} y/o {caseStudy.gender}
                                </Badge>
                                <Badge
                                  bg={
                                    tagBg[caseStudy.tag as keyof typeof tagBg]
                                  }
                                  color={
                                    tagColor[
                                      caseStudy.tag as keyof typeof tagColor
                                    ]
                                  }
                                >
                                  {caseStudy.tag}
                                </Badge>
                              </HStack>
                            </VStack>
                            {/* <HStack spacing={3}>
                              <Tooltip label="Download PDF">
                                <Button
                                  size="sm"
                                  leftIcon={<FaDownload />}
                                  variant="outline"
                                  colorScheme="teal"
                                  onClick={() => handleAction("Download")}
                                >
                                  PDF
                                </Button>
                              </Tooltip>
                              <Tooltip label="Share Case">
                                <Button
                                  size="sm"
                                  leftIcon={<FaShare />}
                                  variant="outline"
                                  colorScheme="blue"
                                  onClick={() => handleAction("Share")}
                                >
                                  Share
                                </Button>
                              </Tooltip>
                              <Tooltip label="Print Summary">
                                <Button
                                  size="sm"
                                  leftIcon={<FaPrint />}
                                  variant="outline"
                                  colorScheme="purple"
                                  onClick={() => handleAction("Print")}
                                >
                                  Print
                                </Button>
                              </Tooltip>
                            </HStack> */}
                          </Flex>

                          {/* Enhanced Case Content */}
                          <Box p={0}>
                            <Grid
                              templateColumns={{ base: "1fr", md: "1fr 1fr" }}
                              gap={0}
                            >
                              {/* Left Column - Core clinical info */}
                              <GridItem
                                borderRight={{ base: "none", md: "1px" }}
                                borderColor={borderColor}
                                p={6}
                              >
                                <VStack align="stretch" spacing={6}>
                                  {/* Chief Complaint */}
                                  <Box>
                                    <HStack mb={3}>
                                      <Icon
                                        as={FaBriefcaseMedical}
                                        color="red.500"
                                        boxSize={5}
                                      />
                                      <Heading
                                        as="h4"
                                        size="sm"
                                        color={highlightColor}
                                      >
                                        Chief Complaint
                                      </Heading>
                                    </HStack>
                                    <Text
                                      fontWeight="medium"
                                      pl={8}
                                      borderLeft="2px"
                                      borderColor="red.200"
                                    >
                                      {caseStudy.chiefComplaint}
                                    </Text>
                                  </Box>

                                  {/* Diagnosis */}
                                  <Box>
                                    <HStack mb={3}>
                                      <Icon
                                        as={FaStethoscope}
                                        color="blue.500"
                                        boxSize={5}
                                      />
                                      <Heading
                                        as="h4"
                                        size="sm"
                                        color={highlightColor}
                                      >
                                        Diagnosis
                                      </Heading>
                                    </HStack>
                                    <Text
                                      pl={8}
                                      borderLeft="2px"
                                      borderColor="blue.200"
                                    >
                                      {caseStudy.diagnosis}
                                    </Text>
                                  </Box>

                                  {/* Symptoms */}
                                  <Box>
                                    <HStack mb={3}>
                                      <Icon
                                        as={FaClipboardList}
                                        color="orange.500"
                                        boxSize={5}
                                      />
                                      <Heading
                                        as="h4"
                                        size="sm"
                                        color={highlightColor}
                                      >
                                        Symptoms
                                      </Heading>
                                    </HStack>
                                    <List spacing={2} pl={8}>
                                      {caseStudy.symptoms.map(
                                        (symptom, idx) => (
                                          <ListItem
                                            key={idx}
                                            display="flex"
                                            alignItems="baseline"
                                          >
                                            <Icon
                                              as={MdOutlineMedicalServices}
                                              color="orange.500"
                                              mr={2}
                                            />
                                            <Text>{symptom}</Text>
                                          </ListItem>
                                        )
                                      )}
                                    </List>
                                  </Box>

                                  {/* History of Illness */}
                                  {caseStudy.historyOfIllness &&
                                    caseStudy.historyOfIllness.length > 0 && (
                                      <Box>
                                        <HStack mb={3}>
                                          <Icon
                                            as={FaHistory}
                                            color="purple.500"
                                            boxSize={5}
                                          />
                                          <Heading
                                            as="h4"
                                            size="sm"
                                            color={highlightColor}
                                          >
                                            History of Illness
                                          </Heading>
                                        </HStack>
                                        <List spacing={2} pl={8}>
                                          {caseStudy.historyOfIllness.map(
                                            (item, idx) => (
                                              <ListItem
                                                key={idx}
                                                display="flex"
                                                alignItems="baseline"
                                              >
                                                <Icon
                                                  as={MdOutlineMedicalServices}
                                                  color="purple.500"
                                                  mr={2}
                                                />
                                                <Text>{item}</Text>
                                              </ListItem>
                                            )
                                          )}
                                        </List>
                                      </Box>
                                    )}

                                  {/* Injuries for trauma case */}
                                  {caseStudy.injuries && (
                                    <Box>
                                      <HStack mb={3}>
                                        <Icon
                                          as={IoIosBody}
                                          color="red.500"
                                          boxSize={5}
                                        />
                                        <Heading
                                          as="h4"
                                          size="sm"
                                          color={highlightColor}
                                        >
                                          Injuries
                                        </Heading>
                                      </HStack>
                                      <List spacing={2} pl={8}>
                                        {caseStudy.injuries.map(
                                          (injury, idx) => (
                                            <ListItem
                                              key={idx}
                                              display="flex"
                                              alignItems="baseline"
                                            >
                                              <Icon
                                                as={MdOutlineMedicalServices}
                                                color="red.500"
                                                mr={2}
                                              />
                                              <Text>{injury}</Text>
                                            </ListItem>
                                          )
                                        )}
                                      </List>
                                    </Box>
                                  )}
                                </VStack>
                              </GridItem>

                              {/* Right Column - Treatment & additional */}
                              <GridItem p={6}>
                                <VStack align="stretch" spacing={6}>
                                  {/* Treatment Plan */}
                                  <Box>
                                    <HStack mb={3}>
                                      <Icon
                                        as={FaProcedures}
                                        color="green.500"
                                        boxSize={5}
                                      />
                                      <Heading
                                        as="h4"
                                        size="sm"
                                        color={highlightColor}
                                      >
                                        Treatment Plan
                                      </Heading>
                                    </HStack>
                                    {Array.isArray(caseStudy.treatmentPlan) ? (
                                      <List spacing={2} pl={8}>
                                        {caseStudy.treatmentPlan.map(
                                          (item, idx) => (
                                            <ListItem
                                              key={idx}
                                              display="flex"
                                              alignItems="baseline"
                                            >
                                              <Icon
                                                as={MdOutlineMedicalServices}
                                                color="green.500"
                                                mr={2}
                                              />
                                              <Text>{item}</Text>
                                            </ListItem>
                                          )
                                        )}
                                      </List>
                                    ) : (
                                      <Text
                                        pl={8}
                                        borderLeft="2px"
                                        borderColor="green.200"
                                      >
                                        {caseStudy.treatmentPlan}
                                      </Text>
                                    )}
                                  </Box>

                                  {/* Vital Signs */}
                                  {caseStudy.vitalSigns && (
                                    <Box>
                                      <HStack mb={3}>
                                        <Icon
                                          as={FaHeartbeat}
                                          color="red.500"
                                          boxSize={5}
                                        />
                                        <Heading
                                          as="h4"
                                          size="sm"
                                          color={highlightColor}
                                        >
                                          Vital Signs
                                        </Heading>
                                      </HStack>
                                      <Grid
                                        templateColumns="auto 1fr"
                                        gap={3}
                                        alignItems="start"
                                        pl={8}
                                      >
                                        {caseStudy.vitalSigns.pulseRate && (
                                          <>
                                            <Text fontWeight="medium">
                                              Pulse:
                                            </Text>
                                            <Text>
                                              {caseStudy.vitalSigns.pulseRate}
                                            </Text>
                                          </>
                                        )}
                                        {caseStudy.vitalSigns.bp && (
                                          <>
                                            <Text fontWeight="medium">BP:</Text>
                                            <Text>
                                              {caseStudy.vitalSigns.bp}
                                            </Text>
                                          </>
                                        )}
                                        {caseStudy.vitalSigns.saturation && (
                                          <>
                                            <Text fontWeight="medium">
                                              O2 Sat:
                                            </Text>
                                            <Text>
                                              {caseStudy.vitalSigns.saturation}
                                            </Text>
                                          </>
                                        )}
                                        {caseStudy.vitalSigns.initialBP && (
                                          <>
                                            <Text fontWeight="medium">
                                              Initial BP:
                                            </Text>
                                            <Text>
                                              {caseStudy.vitalSigns.initialBP}
                                            </Text>
                                          </>
                                        )}
                                        {caseStudy.vitalSigns.intraopBP && (
                                          <>
                                            <Text fontWeight="medium">
                                              Intraop BP:
                                            </Text>
                                            <Text>
                                              {caseStudy.vitalSigns.intraopBP}
                                            </Text>
                                          </>
                                        )}
                                        {caseStudy.vitalSigns.finalBP && (
                                          <>
                                            <Text fontWeight="medium">
                                              Final BP:
                                            </Text>
                                            <Text>
                                              {caseStudy.vitalSigns.finalBP}
                                            </Text>
                                          </>
                                        )}
                                      </Grid>
                                    </Box>
                                  )}

                                  {/* Allergies */}
                                  {caseStudy.allergies &&
                                    caseStudy.allergies.length > 0 && (
                                      <Box>
                                        <HStack mb={3}>
                                          <Icon
                                            as={FaSyringe}
                                            color="red.500"
                                            boxSize={5}
                                          />
                                          <Heading
                                            as="h4"
                                            size="sm"
                                            color={highlightColor}
                                          >
                                            Allergies
                                          </Heading>
                                        </HStack>
                                        <Flex flexWrap="wrap" gap={2} pl={8}>
                                          {caseStudy.allergies.map(
                                            (allergy, idx) => (
                                              <Badge
                                                key={idx}
                                                colorScheme="red"
                                                px={2}
                                                py={1}
                                                borderRadius="md"
                                                variant="subtle"
                                              >
                                                {allergy}
                                              </Badge>
                                            )
                                          )}
                                        </Flex>
                                      </Box>
                                    )}

                                  {/* Medications */}
                                  {caseStudy.medications &&
                                    caseStudy.medications.length > 0 && (
                                      <Box>
                                        <HStack mb={3}>
                                          <Icon
                                            as={FaSyringe}
                                            color="blue.500"
                                            boxSize={5}
                                          />
                                          <Heading
                                            as="h4"
                                            size="sm"
                                            color={highlightColor}
                                          >
                                            Medications
                                          </Heading>
                                        </HStack>
                                        <List spacing={2} pl={8}>
                                          {caseStudy.medications.map(
                                            (medication, idx) => (
                                              <ListItem
                                                key={idx}
                                                display="flex"
                                                alignItems="baseline"
                                              >
                                                <Icon
                                                  as={MdOutlineMedicalServices}
                                                  color="blue.500"
                                                  mr={2}
                                                />
                                                <Text>{medication}</Text>
                                              </ListItem>
                                            )
                                          )}
                                        </List>
                                      </Box>
                                    )}

                                  {/* Anesthesia Details */}
                                  {caseStudy.anesthesiaDetails && (
                                    <Box>
                                      <HStack mb={3}>
                                        <Icon
                                          as={FaSyringe}
                                          color="teal.500"
                                          boxSize={5}
                                        />
                                        <Heading
                                          as="h4"
                                          size="sm"
                                          color={highlightColor}
                                        >
                                          Anesthesia
                                        </Heading>
                                      </HStack>
                                      <Grid
                                        templateColumns="auto 1fr"
                                        gap={3}
                                        alignItems="start"
                                        pl={8}
                                      >
                                        <Text fontWeight="medium">
                                          Primary:
                                        </Text>
                                        <Text>
                                          {caseStudy.anesthesiaDetails.primary}
                                        </Text>
                                        <Text fontWeight="medium">Rescue:</Text>
                                        <Text>
                                          {caseStudy.anesthesiaDetails.rescue}
                                        </Text>
                                      </Grid>
                                    </Box>
                                  )}

                                  {/* Interventions */}
                                  {caseStudy.interventions && (
                                    <Box>
                                      <HStack mb={3}>
                                        <Icon
                                          as={FaSyringe}
                                          color="blue.500"
                                          boxSize={5}
                                        />
                                        <Heading
                                          as="h4"
                                          size="sm"
                                          color={highlightColor}
                                        >
                                          Interventions
                                        </Heading>
                                      </HStack>
                                      <List spacing={2} pl={8}>
                                        {caseStudy.interventions.map(
                                          (intervention, idx) => (
                                            <ListItem
                                              key={idx}
                                              display="flex"
                                              alignItems="baseline"
                                            >
                                              <Icon
                                                as={MdOutlineMedicalServices}
                                                color="blue.500"
                                                mr={2}
                                              />
                                              <Text>{intervention}</Text>
                                            </ListItem>
                                          )
                                        )}
                                      </List>
                                    </Box>
                                  )}

                                  {/* Disposition */}
                                  {caseStudy.disposition && (
                                    <Box>
                                      <HStack mb={3}>
                                        <Icon
                                          as={FaHospitalAlt}
                                          color="blue.500"
                                          boxSize={5}
                                        />
                                        <Heading
                                          as="h4"
                                          size="sm"
                                          color={highlightColor}
                                        >
                                          Disposition
                                        </Heading>
                                      </HStack>
                                      <Text
                                        pl={8}
                                        borderLeft="2px"
                                        borderColor="blue.200"
                                      >
                                        {caseStudy.disposition}
                                      </Text>
                                    </Box>
                                  )}

                                  {/* Procedure Outcome */}
                                  {caseStudy.procedureOutcome && (
                                    <Box>
                                      <HStack mb={3}>
                                        <Icon
                                          as={IoMdDocument}
                                          color="green.500"
                                          boxSize={5}
                                        />
                                        <Heading
                                          as="h4"
                                          size="sm"
                                          color={highlightColor}
                                        >
                                          Procedure Outcome
                                        </Heading>
                                      </HStack>
                                      <Text
                                        pl={8}
                                        borderLeft="2px"
                                        borderColor="green.200"
                                      >
                                        {caseStudy.procedureOutcome}
                                      </Text>
                                    </Box>
                                  )}

                                  {/* Additional Notes */}
                                  {caseStudy.additionalNotes && (
                                    <Box>
                                      <HStack mb={3}>
                                        <Icon
                                          as={IoMdDocument}
                                          color="orange.500"
                                          boxSize={5}
                                        />
                                        <Heading
                                          as="h4"
                                          size="sm"
                                          color={highlightColor}
                                        >
                                          Additional Notes
                                        </Heading>
                                      </HStack>
                                      <Text
                                        pl={8}
                                        borderLeft="2px"
                                        borderColor="orange.200"
                                      >
                                        {caseStudy.additionalNotes}
                                      </Text>
                                    </Box>
                                  )}
                                </VStack>
                              </GridItem>
                            </Grid>
                          </Box>
                        </Box>
                      </ScaleFade>
                    </TabPanel>
                  ))}
                </TabPanels>
              </Tabs>
            </Box>
          </VStack>
        </Container>
      </Box>

      {/* Enhanced Features Section */}
      <Box as="section" py={20} bg={sectionBg}>
        <Container maxW="container.xl">
          <VStack spacing={12}>
            <Box textAlign="center" maxW="container.md" mx="auto">
              <Badge
                colorScheme="teal"
                px={3}
                py={1}
                mb={4}
                borderRadius="full"
                fontSize="sm"
              >
                Key Benefits
              </Badge>
              <Heading as="h2" size="xl" mb={4} color={highlightColor}>
                Transform Your Clinical Documentation
              </Heading>
              <Text fontSize="lg">
                Experience the power of AI-driven clinical summarization that
                enhances efficiency and accuracy.
              </Text>
            </Box>

            <Grid
              templateColumns={{
                base: "1fr",
                md: "repeat(2, 1fr)",
                lg: "repeat(3, 1fr)",
              }}
              gap={8}
              w="full"
            >
              {/* Feature 1 */}
              <Box
                bg={cardBg}
                p={6}
                borderRadius="lg"
                boxShadow="md"
                borderTop="4px solid"
                borderColor="teal.500"
                transition="all 0.3s"
                _hover={{
                  transform: "translateY(-5px)",
                  boxShadow: "lg",
                }}
              >
                <VStack align="flex-start" spacing={4}>
                  <Flex
                    w={12}
                    h={12}
                    bg="teal.100"
                    color="teal.700"
                    borderRadius="md"
                    justify="center"
                    align="center"
                  >
                    <Icon as={MdOutlineAccessTimeFilled} boxSize={6} />
                  </Flex>
                  <Heading as="h3" size="md">
                    75% Faster Review Time
                  </Heading>
                  <Text>
                    Clinicians can review patient information in a fraction of
                    the time, with key data highlighted and organized for rapid
                    comprehension.
                  </Text>
                </VStack>
              </Box>

              {/* Feature 2 */}
              <Box
                bg={cardBg}
                p={6}
                borderRadius="lg"
                boxShadow="md"
                borderTop="4px solid"
                borderColor="blue.500"
                transition="all 0.3s"
                _hover={{
                  transform: "translateY(-5px)",
                  boxShadow: "lg",
                }}
              >
                <VStack align="flex-start" spacing={4}>
                  <Flex
                    w={12}
                    h={12}
                    bg="blue.100"
                    color="blue.700"
                    borderRadius="md"
                    justify="center"
                    align="center"
                  >
                    <Icon as={FaClipboardList} boxSize={6} />
                  </Flex>
                  <Heading as="h3" size="md">
                    Structured Clinical Data
                  </Heading>
                  <Text>
                    AI identifies and extracts key clinical elements:
                    complaints, symptoms, diagnoses, treatment plans, and
                    patient history in proper medical format.
                  </Text>
                </VStack>
              </Box>

              {/* Feature 3 */}
              <Box
                bg={cardBg}
                p={6}
                borderRadius="lg"
                boxShadow="md"
                borderTop="4px solid"
                borderColor="purple.500"
                transition="all 0.3s"
                _hover={{
                  transform: "translateY(-5px)",
                  boxShadow: "lg",
                }}
              >
                <VStack align="flex-start" spacing={4}>
                  <Flex
                    w={12}
                    h={12}
                    bg="purple.100"
                    color="purple.700"
                    borderRadius="md"
                    justify="center"
                    align="center"
                  >
                    <Icon as={FaStethoscope} boxSize={6} />
                  </Flex>
                  <Heading as="h3" size="md">
                    Specialty-Specific Context
                  </Heading>
                  <Text>
                    Summaries reflect medical specialty nuances, highlighting
                    relevant information for cardiology, oncology, emergency
                    medicine, and more.
                  </Text>
                </VStack>
              </Box>

              {/* Feature 4 */}
              <Box
                bg={cardBg}
                p={6}
                borderRadius="lg"
                boxShadow="md"
                borderTop="4px solid"
                borderColor="green.500"
                transition="all 0.3s"
                _hover={{
                  transform: "translateY(-5px)",
                  boxShadow: "lg",
                }}
              >
                <VStack align="flex-start" spacing={4}>
                  <Flex
                    w={12}
                    h={12}
                    bg="green.100"
                    color="green.700"
                    borderRadius="md"
                    justify="center"
                    align="center"
                  >
                    <Icon as={FaFileMedicalAlt} boxSize={6} />
                  </Flex>
                  <Heading as="h3" size="md">
                    EHR Integration
                  </Heading>
                  <Text>
                    Seamlessly export structured summaries to all major
                    electronic health record systems with proper formatting and
                    coding.
                  </Text>
                </VStack>
              </Box>

              {/* Feature 5 */}
              <Box
                bg={cardBg}
                p={6}
                borderRadius="lg"
                boxShadow="md"
                borderTop="4px solid"
                borderColor="red.500"
                transition="all 0.3s"
                _hover={{
                  transform: "translateY(-5px)",
                  boxShadow: "lg",
                }}
              >
                <VStack align="flex-start" spacing={4}>
                  <Flex
                    w={12}
                    h={12}
                    bg="red.100"
                    color="red.700"
                    borderRadius="md"
                    justify="center"
                    align="center"
                  >
                    <Icon as={FaUser} boxSize={6} />
                  </Flex>
                  <Heading as="h3" size="md">
                    Privacy Protection
                  </Heading>
                  <Text>
                    Automatically redacts or de-identifies sensitive patient
                    information while maintaining clinical relevance and
                    meaning.
                  </Text>
                </VStack>
              </Box>

              {/* Feature 6 */}
              <Box
                bg={cardBg}
                p={6}
                borderRadius="lg"
                boxShadow="md"
                borderTop="4px solid"
                borderColor="orange.500"
                transition="all 0.3s"
                _hover={{
                  transform: "translateY(-5px)",
                  boxShadow: "lg",
                }}
              >
                <VStack align="flex-start" spacing={4}>
                  <Flex
                    w={12}
                    h={12}
                    bg="orange.100"
                    color="orange.700"
                    borderRadius="md"
                    justify="center"
                    align="center"
                  >
                    <Icon as={FaHistory} boxSize={6} />
                  </Flex>
                  <Heading as="h3" size="md">
                    Timeline Visualization
                  </Heading>
                  <Text>
                    Generates chronological views of patient history,
                    highlighting significant events and treatment milestones for
                    better continuity of care.
                  </Text>
                </VStack>
              </Box>
            </Grid>
          </VStack>
        </Container>
      </Box>

      {/* Enhanced CTA Section */}
      <Box as="section" py={16} bg={cardBg}>
        <Container maxW="container.lg">
          <VStack spacing={8}>
            <Box textAlign="center" maxW="container.md" mx="auto">
              <Badge
                colorScheme="teal"
                px={3}
                py={1}
                mb={4}
                borderRadius="full"
                fontSize="sm"
              >
                Ready to Get Started?
              </Badge>
              <Heading
                as="h2"
                size="xl"
                fontWeight="bold"
                bg="linear-gradient(90deg, #3182CE 0%, #38B2AC 100%)"
                bgClip="text"
                mb={4}
              >
                Transform Your Clinical Documentation Today
              </Heading>
              <Text fontSize="lg" color="gray.600">
                Join thousands of healthcare professionals who are saving time
                and improving care quality with AI-powered clinical
                summarization.
              </Text>
            </Box>
            <HStack spacing={4}>
              <Button
                size="lg"
                colorScheme="teal"
                px={8}
                py={6}
                borderRadius="md"
                fontWeight="bold"
                onClick={() => handleCTA("signup")}
                rightIcon={<IoMdArrowForward />}
                _hover={{
                  transform: "translateY(-2px)",
                  boxShadow: "lg",
                }}
              >
                Start Free Trial
              </Button>
              <Button
                size="lg"
                variant="outline"
                colorScheme="teal"
                px={8}
                py={6}
                borderRadius="md"
                onClick={() => handleCTA("demo")}
                _hover={{
                  transform: "translateY(-2px)",
                  boxShadow: "md",
                }}
              >
                Request Demo
              </Button>
            </HStack>
          </VStack>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
}

export default ClinicalSummaries;
